import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export interface PostPayload {
  title?: string;
  content: string;
  contentType: 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'POLL' | 'LINK';
  isPublic: boolean;
  isScheduled: boolean;
  publishedAt?: string | null;
  tierIds?: string[];
  tags?: string[];
  images?: File[];
  pollOptions?: string[]; // For POLL content type
}

export const postsService = {
  async list(params: { query?: string; limit?: number; offset?: number; creatorId?: string; isPublic?: boolean }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/posts`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      params,
    });
    return res.data as { posts: any[]; pagination: any };
  },

  async get(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/posts/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async create(payload: PostPayload) {
    const token = localStorage.getItem('accessToken');
    // For TEXT-only, send JSON. For media, use FormData
    if (payload.images && payload.images.length > 0) {
      const form = new FormData();
      if (payload.title) form.append('title', payload.title);
      form.append('content', payload.content);
      form.append('contentType', payload.contentType);
      form.append('isPublic', String(payload.isPublic));
      form.append('isScheduled', String(payload.isScheduled));
      if (payload.publishedAt) form.append('publishedAt', payload.publishedAt);
      if (payload.tierIds) payload.tierIds.forEach(t => form.append('tierIds[]', t));
      if (payload.pollOptions) payload.pollOptions.forEach(opt => form.append('pollOptions[]', opt));
      payload.images.forEach(file => form.append('images', file));

      const res = await axios.post(`${API_BASE_URL}/api/posts`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    }

    const res = await axios.post(`${API_BASE_URL}/api/posts`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async vote(postId: string, optionIndex: number) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/posts/${postId}/vote`, { optionIndex }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async getPollResults(postId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/posts/${postId}/poll-results`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return res.data as {
      totalVotes: number;
      options: Array<{ option: string; votes: number; percentage: number }>;
      userVote: number | null;
    };
  },

  async update(id: string, payload: Partial<PostPayload>) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/posts/${id}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async remove(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.delete(`${API_BASE_URL}/api/posts/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async like(postId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/posts/${postId}/like`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { message: string; isLiked: boolean };
  },

  async togglePin(postId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/posts/${postId}/pin`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { message: string; post: any };
  },

  async getEditHistory(postId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/posts/${postId}/history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { history: Array<{ id: string; title?: string; content: string; editedBy: string; editedAt: string }> };
  },
};



